import React, { useContext, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import CommonTabSwitch, { TabSwitchItem } from '../../Components/CommonTabSwitch/CommonTabSwitch';
import SupplierTabContext from './SupplierTabContext';

const TAB_USERS = 'users';
const TAB_FACTORIES = 'component-suppliers';
const TAB_SUPPORTING_DOCS = 'supporting-docs';

interface SupplierTabNavProps {
  inlineContent?: React.ReactNode;
  trailingContent?: React.ReactNode;
}

/**
 * Partner Management tab strip — Users / Factories / Supporting Documents.
 * Renders the shared segmented pill switch; the active tab is derived from the
 * current pathname and a pick pushes the matching supplier sub-route, so the
 * tabs stay bookmarkable exactly as the previous `<Link>` list was.
 * The design runs the tab row edge to edge: tabs on the left, a page-supplied
 * notice beside them, and a page-supplied control at the far right (Figma node
 * 12385:216053). `inlineContent` and `trailingContent` are those two slots.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.inlineContent - Rendered immediately after the tabs.
 * @param {React.ReactNode} props.trailingContent - Rendered at the far right of the row.
 * @returns {React.ReactElement|null} The tab strip, or null while the context is
 *   absent or the viewer is a logged-in supplier (who sees no tab chrome).
 */
function SupplierTabNav({ inlineContent, trailingContent }: SupplierTabNavProps) {
  const ctx = useContext(SupplierTabContext);
  const { t } = useTranslation('modules/partner-overview');
  const { pathname } = useLocation();
  const history = useHistory();

  const isProductionLocationTier = ctx?.isProductionLocationTier;
  const supplierId = ctx?.supplierId;

  const menuList = useMemo(() => {
    const tabs: TabSwitchItem[] = [];
    if (!isProductionLocationTier) {
      tabs.push({ eventKey: TAB_USERS, eventName: t('SUPPLIER_DOCUMENT.USERS') });
    }
    tabs.push({
      eventKey: TAB_FACTORIES,
      eventName: isProductionLocationTier ? t('NEW_PARTNER_OVERVIEW.PRODUCTION_lOCATION') : t('SUPPLIER_DOCUMENT.FACTORIES'),
    });
    tabs.push({ eventKey: TAB_SUPPORTING_DOCS, eventName: t('SUPPLIER_DOCUMENT.SUPPORTING_DOCUMENT') });
    return tabs;
  }, [isProductionLocationTier, t]);

  const activeTab = useMemo(() => {
    if (pathname.includes(`/${TAB_USERS}`)) return TAB_USERS;
    if (pathname.includes(TAB_FACTORIES)) return TAB_FACTORIES;
    if (pathname.includes(TAB_SUPPORTING_DOCS)) return TAB_SUPPORTING_DOCS;
    return '';
  }, [pathname]);

  const activeTheTab = useCallback(
    (eventKey: string) => {
      history.push(`/supplier/${supplierId}/${eventKey}?supplierId=${supplierId}`);
    },
    [history, supplierId],
  );

  if (!ctx || ctx.isSupplierLoggedIn) return null;

  return (
    <Container fluid>
      <div className="d-flex align-items-center mt-3 partner-tab-row">
        <CommonTabSwitch masterActiveTab={activeTab} MenuList={menuList} activeTheTab={activeTheTab} />
        {inlineContent}
        {trailingContent ? <div className="ml-auto d-flex align-items-center">{trailingContent}</div> : null}
      </div>
    </Container>
  );
}

export default SupplierTabNav;
